import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LoadScript } from "@react-google-maps/api";
import "@/App.css";

const GOOGLE_MAPS_LIBRARIES = ["drawing"];

import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import DashboardPage from "./pages/DashboardPage";
import LeadsPage from "./pages/LeadsPage";
import LeadsResultsPage from "./pages/LeadsResultsPage";
import MapHelpPage from "./pages/MapHelpPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import ProfilePage from "./pages/ProfilePage";
import IntegrationsPage from "./pages/IntegrationsPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import AdminSubscriptionsPage from "./pages/AdminSubscriptionsPage";
import AdminAbandonedCartsPage from "./pages/AdminAbandonedCartsPage";
import PriceConfigurePage from "./pages/PriceConfigurePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ExternalRedirect from "./components/ExternalRedirect";

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

export default function App() {
  return (
    <div className="App">
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={GOOGLE_MAPS_LIBRARIES}
        version="3.64"
      >
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route
                path="/pricing/configure"
                element={<Navigate to="/pricing/new" replace />}
              />
              <Route path="/pricing/new" element={<PriceConfigurePage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads"
                element={
                  <ProtectedRoute>
                    <LeadsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads/results"
                element={
                  <ProtectedRoute>
                    <LeadsResultsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads/map-help"
                element={
                  <ProtectedRoute>
                    <MapHelpPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads/:leadId"
                element={
                  <ProtectedRoute>
                    <LeadDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute>
                    <SubscriptionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute>
                    <IntegrationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin-settings" element={<AdminSettingsPage />} />
              <Route
                path="/admin/subscriptions"
                element={
                  <ProtectedRoute>
                    <AdminSubscriptionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/abandoned-carts"
                element={
                  <ProtectedRoute>
                    <AdminAbandonedCartsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={<ExternalRedirect to="/api/admin/" />}
              />
              <Route
                path="/admin/*"
                element={<ExternalRedirect to="/api/admin/" />}
              />
              <Route
                path="/docs"
                element={<ExternalRedirect to="/api/docs" />}
              />
              <Route
                path="/redoc"
                element={<ExternalRedirect to="/api/redoc" />}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </LoadScript>
    </div>
  );
}
